import React, { useState, useEffect } from 'react';
import { apiGet } from '../utils/api';

interface Account {
  id: string;
  name: string;
  type: string;
  currency: string;
  balance: number;
}

interface AccountTypeSelectorProps {
  value: string;
  onChange: (accountId: string) => void;
  required?: boolean;
}

const AccountTypeSelector: React.FC<AccountTypeSelectorProps> = ({
  value,
  onChange,
  required = false
}) => {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [selectedType, setSelectedType] = useState<string>('');
  const [loading, setLoading] = useState(true);

  // Загружаем все счета при монтировании компонента
  useEffect(() => {
    fetchAccounts();
  }, []);

  // Инициализируем selectedType на основе переданного value
  useEffect(() => {
    if (value && accounts.length > 0) {
      const selectedAccount = accounts.find(account => account.id === value);
      if (selectedAccount) {
        setSelectedType(selectedAccount.type);
      }
    }
  }, [value, accounts]);

  const fetchAccounts = async () => {
    try {
      const data = await apiGet<Account[]>('/cash-accounts');
      console.log('Загружены счета:', data);
      setAccounts(data);
    } catch (error: any) {
      console.error('Ошибка загрузки счетов:', error);
    } finally {
      setLoading(false);
    }
  };

  // Получаем уникальные типы счетов
  const accountTypes = Array.from(new Set(accounts.map(account => account.type)));

  // Получаем счета выбранного типа
  const filteredAccounts = selectedType 
    ? accounts.filter(account => account.type === selectedType)
    : [];

  // Обработчик изменения типа счета
  const handleTypeChange = (type: string) => {
    setSelectedType(type);
    
    // Ищем счет по умолчанию для выбранного типа
    const defaultAccount = accounts.find(account => 
      account.type === type && account.isDefault && account.isActive
    );
    
    if (defaultAccount) {
      // Автоматически выбираем счет по умолчанию
      onChange(defaultAccount.id);
    } else {
      // Если нет счета по умолчанию, сбрасываем выбор
      onChange('');
    }
  };

  // Обработчик изменения счета
  const handleAccountChange = (accountId: string) => {
    onChange(accountId);
  };

  // Функция для получения метки типа счета
  const getTypeLabel = (type: string) => {
    const typeLabels: { [key: string]: string } = {
      'CASH': 'Касса',
      'BANK': 'Банковский счет',
      'CARD': 'Банковская карта',
      'EXTERNAL': 'Внешний счет'
    };
    return typeLabels[type] || type;
  };

  if (loading) {
    return (
      <div className="space-y-2">
        <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
        <div className="h-10 bg-gray-200 rounded animate-pulse"></div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {/* Выбор типа счета и счета в одной строке */}
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="block text-xs text-gray-600 mb-1">
            Тип счета {required && <span className="text-red-500">*</span>}
          </label>
          <select
            value={selectedType}
            onChange={(e) => handleTypeChange(e.target.value)}
            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            required={required}
          >
            <option value="">Выберите тип</option>
            {accountTypes.map((type) => (
              <option key={type} value={type}>
                {getTypeLabel(type)}
              </option>
            ))}
          </select>
        </div>

        {/* Выбор конкретного счета */}
        <div>
          <label className="block text-xs text-gray-600 mb-1">
            Счет {required && <span className="text-red-500">*</span>}
          </label>
          <select
            value={value}
            onChange={(e) => handleAccountChange(e.target.value)}
            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            required={required}
            disabled={!selectedType}
          >
            <option value="">Выберите счет</option>
            {filteredAccounts.map((account) => (
              <option key={account.id} value={account.id}>
                {account.name} ({account.currency}) - {account.balance.toFixed(2)}{account.isDefault ? ' •' : ''}
              </option>
            ))}
          </select>
        </div>
      </div>

    </div>
  );
};

export default AccountTypeSelector;
